import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type InteractionChannel = 'whatsapp' | 'call' | 'email' | 'meeting' | 'sms' | 'other';
export type InteractionDirection = 'outbound' | 'inbound';
export type InteractionOutcome = 'positive_response' | 'objection' | 'no_response' | 'requested_time' | 'closed' | 'gave_up';
export type ObjectionType = 'price_discount' | 'price_installment' | 'timing' | 'spouse' | 'trust' | 'competitor' | 'documentation' | 'other';

export interface Interaction {
  id: string;
  deal_id: string;
  contact_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  channel: InteractionChannel | null;
  direction: InteractionDirection | null;
  outcome: InteractionOutcome | null;
  objection_type: ObjectionType | null;
  objection_details: string | null;
  next_action: string | null;
  next_action_date: string | null;
  notes: string | null;
  created_by: string | null;
}

export interface CreateInteractionInput {
  deal_id: string;
  contact_id?: string | null;
  channel: InteractionChannel;
  direction: InteractionDirection;
  outcome?: InteractionOutcome | null;
  objection_type?: ObjectionType | null;
  objection_details?: string | null;
  next_action?: string | null;
  next_action_date?: string | null;
  notes?: string | null;
}

export interface UpdateInteractionInput {
  channel?: InteractionChannel;
  direction?: InteractionDirection;
  outcome?: InteractionOutcome | null;
  objection_type?: ObjectionType | null;
  objection_details?: string | null;
  next_action?: string | null;
  next_action_date?: string | null;
  notes?: string | null;
}

export function useInteractions(dealId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['interactions', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interactions')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Interaction[];
    },
    enabled: !!dealId,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateInteractionInput) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      const { data: appUser } = user 
        ? await supabase.from('app_users').select('id').eq('auth_user_id', user.id).single()
        : { data: null };

      const { data, error } = await supabase
        .from('interactions')
        .insert({
          ...input,
          created_by: appUser?.id || null,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions', dealId] });
      toast.success('Interação registrada');
    },
    onError: (error) => {
      console.error('Error creating interaction:', error);
      toast.error('Erro ao registrar interação');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateInteractionInput & { id: string }) => {
      const { data, error } = await supabase
        .from('interactions')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions', dealId] });
      toast.success('Interação atualizada');
    },
    onError: (error) => {
      console.error('Error updating interaction:', error);
      toast.error('Erro ao atualizar interação');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('interactions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions', dealId] });
      toast.success('Interação excluída');
    },
    onError: (error) => {
      console.error('Error deleting interaction:', error);
      toast.error('Erro ao excluir interação');
    },
  });

  return {
    interactions: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    createInteraction: createMutation.mutate,
    updateInteraction: updateMutation.mutate,
    deleteInteraction: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
